'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { useReactiveFeatureFlags } from '@/app/contexts';
import { canShowCaseManagementNav } from '@/shared/case-management-nav-access';
import { fetchProvenanceStats } from '@/shared/cors-provenance-api';
import { captureError } from '@/shared/functions';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import './work-product-subnav.css';
import './case-management-dashboard.css';

const UserManagementDashboard = dynamic(
  () => import('@/components/user-management-dashboard'),
  { ssr: false, loading: () => null },
);
const CaseManagementDashboard = dynamic(
  () => import('@/components/case-management-dashboard'),
  { ssr: false, loading: () => null },
);

export type AccountProps = {
  userId: string;
  isUserAdmin: boolean;
  isPermissionServiceAvailable: boolean;
  onOpenCase?: (caseName: string, destination: 'chat' | 'case-file-management') => void;
};

type OrgUsage = {
  usageInBytes: number;
  quotaInBytes: number;
};

const GIB = 1024 ** 3;

function formatBytesAsGb(bytes: number): string {
  const gb = bytes / GIB;
  if (!Number.isFinite(gb) || gb < 0) return '—';
  if (gb === 0) return '0 GB';
  if (gb < 0.01) return '< 0.01 GB';
  return `${gb.toFixed(2)} GB`;
}

function IdentityRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="m-0 border-t border-white/[0.06] py-1.5 text-sm text-gray-500">{label}</dt>
      <dd className="m-0 border-t border-white/[0.06] py-1.5 pl-6 text-right text-sm text-gray-200 break-words">
        {value}
      </dd>
    </>
  );
}

export function Account({
  userId,
  isUserAdmin,
  isPermissionServiceAvailable,
  onOpenCase,
}: AccountProps) {
  const t = useTimePilotT();
  const featureFlags = useReactiveFeatureFlags();
  const { cellId, accessToken: realAccessToken, enabled: cellSessionEnabled } =
    useCellServiceSession();
  const [metering, setMetering] = useState<OrgUsage | null>(null);

  const roleLabel = isUserAdmin ? t('account.administrator') : t('account.user');
  const identitySubtitle = `${userId || '—'}, ${roleLabel}`;

  const showCaseManagement = canShowCaseManagementNav({
    isPermissionServiceAvailable,
    isUserAdmin,
    caseManagementDashboard: featureFlags.caseManagementDashboard === true,
  });
  const showUserManagement =
    Boolean(featureFlags.useUserService) && isPermissionServiceAvailable && isUserAdmin;
  const showOrgUsage =
    Boolean(featureFlags.metering) &&
    !!metering &&
    metering.usageInBytes >= 0 &&
    !!metering.quotaInBytes;

  const fetchMetering = useCallback(
    async (signal?: AbortSignal) => {
      if (!cellId || !realAccessToken) return;
      try {
        const data = await fetchProvenanceStats({
          cellId,
          accessToken: realAccessToken,
          signal,
        });
        if (signal?.aborted) return;
        if (data) {
          setMetering({
            usageInBytes: data.totalSizeBytes || 0,
            quotaInBytes: data.quotaLimitBytes || 0,
          });
        }
      } catch (error) {
        if (signal?.aborted) return;
        captureError(`Error fetching metering info: ${error}`, error as Error);
      }
    },
    [cellId, realAccessToken],
  );

  useEffect(() => {
    setMetering(null);
    if (featureFlags.metering && cellSessionEnabled) {
      const controller = new AbortController();
      fetchMetering(controller.signal);
      return () => controller.abort();
    }
  }, [fetchMetering, featureFlags.metering, cellSessionEnabled]);

  return (
    <>
      <nav
        id="account-nav-bar"
        role="navigation"
        aria-label={t('account.navigation')}
        className="tp-secondary-nav tp-secondary-nav--docked inset-x-0 z-40 border-b border-white/[0.06] fixed top-[50px] sm:top-[67px]"
        style={{ backgroundColor: '#060F1B' }}
      >
        <div className="tp-secondary-nav__start tp-secondary-nav__start--column flex-1">
          <span className="truncate text-2xl font-bold text-white">{t('nav.account')}</span>
          <span className="truncate text-sm italic text-white/65">
            {identitySubtitle}
          </span>
        </div>
      </nav>

      <div className="flex flex-col gap-6 px-4 sm:px-5 py-4 sm:py-6">
        {showUserManagement && <UserManagementDashboard />}

        {showCaseManagement && (
          <CaseManagementDashboard
            layout="section"
            onOpenCase={onOpenCase}
            caseFilesEnabled={featureFlags.caseFileManagement === true}
          />
        )}

        {showOrgUsage && metering && (
          <div className="tp-case-mgmt">
            <section
              className="tp-case-mgmt__surface"
              aria-labelledby="account-org-metrics-heading"
            >
              <div className="p-4 sm:p-6">
                <h2
                  id="account-org-metrics-heading"
                  className="m-0 mb-2 text-xs font-medium uppercase tracking-wide text-gray-500"
                >
                  {t('account.orgMetrics')}
                </h2>
                <dl className="m-0 grid grid-cols-[auto_1fr] items-baseline border-b border-white/[0.06]">
                  <IdentityRow label={t('account.used')} value={formatBytesAsGb(metering.usageInBytes)} />
                  <IdentityRow label={t('account.capacity')} value={formatBytesAsGb(metering.quotaInBytes)} />
                </dl>
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
}

export default Account;
